import React, { useState, useEffect, useRef } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY, MAP_ID } from "../../config";
import { useMapColorScheme } from "../../hooks/useMapColorScheme";

const ClockedInLocationMap = ({ clockedInLocationData }) => {
  const colorScheme = useMapColorScheme();
  const [visibleInfoWindowIndex, setVisibleInfoWindowIndex] = useState(null);
  const mapRef = useRef(null); // Create a ref for the map instance
  const [center, setCenter] = useState(null);
  const [zoom, setZoom] = useState(10);
  const [camera, setCamera] = useState({ center: null, zoom: 10 });
  const userHasInteracted = useRef(false);

  const toggleInfoWindow = (index) => {
    setVisibleInfoWindowIndex(visibleInfoWindowIndex === index ? null : index);
  };

  const getDefaultBounds = () => {
    if (clockedInLocationData.length === 0) {
      return null; // default bounds when no data
    }
    const latitudes = clockedInLocationData.map((loc) => loc.latitude);
    const longitudes = clockedInLocationData.map((loc) => loc.longitude);
    const north = Math.max(...latitudes);
    const south = Math.min(...latitudes);
    const east = Math.max(...longitudes);
    const west = Math.min(...longitudes);

    // If all points are the same, add some padding
    const latDiff = north - south;
    const lngDiff = east - west;
    const padding = Math.max(latDiff, lngDiff, 0.01) * 0.1; // Minimum 0.01 degree padding

    return {
      north: north + padding,
      south: south - padding,
      east: east + padding,
      west: west - padding,
    };
  };

  const mapKey = clockedInLocationData.reduce((key, loc) => {
    return key + `${loc.latitude}${loc.longitude}`;
  }, "");

  const defaultBounds = getDefaultBounds();

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
  };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div>
        <Map
          key={`${mapKey}-${colorScheme}`} // Remount when data changes significantly or the theme (colorScheme) switches
          defaultBounds={defaultBounds}
          style={{ width: "100%", height: "350px" }}
          mapId={MAP_ID}
          colorScheme={colorScheme}
          options={mapOptions}
        >
          {clockedInLocationData.map((data, index) => (
            <React.Fragment key={index}>
              <AdvancedMarker
                position={{ lat: data.latitude, lng: data.longitude }}
                label={data.name}
                onClick={() => toggleInfoWindow(index)}
              >
                <img
                  src={data.profile_url}
                  alt={data.name}
                  className="w-12 h-12 rounded-full border-2 border-white drop-shadow-200"
                />
              </AdvancedMarker>
              {visibleInfoWindowIndex === index && (
                <InfoWindow
                  position={{ lat: data.latitude, lng: data.longitude }}
                  onClose={() => setVisibleInfoWindowIndex(null)}
                  className="bg-white p-1 rounded-lg shadow"
                  options={{ pixelOffset: { width: 0, height: -55 } }}
                >
                  <div>
                    <div className="pb-1 uppercase font-medium tracking-wider text-xs text-gray-500">
                      {data.customer_name || "Unknown Customer"}
                    </div>
                    <h2 className="pb-1 font-medium text-md">{data.name}</h2>
                    <p className="text-sm text-gray-600 pb-2">
                      {data.site_name || "Unknown Site"}
                    </p>
                    <p className="text-xs text-gray-500">
                      Clocked in:{" "}
                      {new Date(data.clocked_in_at).toLocaleString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: false,
                      })}
                    </p>
                  </div>
                </InfoWindow>
              )}
            </React.Fragment>
          ))}
        </Map>
      </div>
    </APIProvider>
  );
};

export { ClockedInLocationMap };
